// src/services/indexedDB/core/base-repository.ts
// 通用 CRUD 基礎類別

import { initDB } from './database';
import {
  BaseEntity,
  CachedEntity,
  CacheStats,
  CleanupOptions,
  CleanupResult,
  QueryOptions,
} from './types';
import { logError, logInfo, logWarn } from '../../../utils/logger';

/**
 * 抽象基礎 Repository 類別
 * 封裝所有 IndexedDB 實體的通用 CRUD 操作
 *
 * @template T - 實體類型（必須繼承 BaseEntity）
 */
export abstract class BaseRepository<T extends BaseEntity> {
  /**
   * Store 名稱（子類必須實作）
   */
  protected abstract readonly storeName: string;

  /**
   * 預設的快取保留天數
   */
  protected readonly defaultRetentionDays: number = 30;

  /**
   * 準備實體以儲存到 IndexedDB
   * 添加快取元數據並序列化日期
   *
   * @param entity - 原始實體
   * @returns 準備好的實體
   */
  protected prepareForStorage(entity: T): CachedEntity<T> {
    const now = Date.now();

    return {
      ...entity,
      cachedAt: now,
      // 確保日期是可序列化的
      createdAt: entity.createdAt instanceof Date ? entity.createdAt.getTime() : entity.createdAt,
      updatedAt: entity.updatedAt
        ? entity.updatedAt instanceof Date
          ? entity.updatedAt.getTime()
          : entity.updatedAt
        : now,
    } as CachedEntity<T>;
  }

  /**
   * 反序列化實體（將儲存格式轉換為應用層格式）
   * 子類可以覆寫此方法以自訂反序列化邏輯
   *
   * @param stored - 儲存的實體
   * @returns 反序列化後的實體
   */
  protected deserialize(stored: CachedEntity<T>): T {
    const { cachedAt, ...entity } = stored;

    return {
      ...entity,
      createdAt:
        typeof entity.createdAt === 'number' ? new Date(entity.createdAt) : entity.createdAt,
      updatedAt: entity.updatedAt
        ? typeof entity.updatedAt === 'number'
          ? new Date(entity.updatedAt)
          : entity.updatedAt
        : undefined,
    } as unknown as T;
  }

  /**
   * 批量保存實體
   *
   * @param userId - 用戶 ID
   * @param entities - 實體陣列
   */
  async saveMany(userId: string, entities: T[]): Promise<void> {
    if (!entities.length) return;

    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      for (const entity of entities) {
        const prepared = this.prepareForStorage({ ...entity, userId } as T);
        store.put(prepared);
      }

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      logError(`Failed to save ${this.storeName} to IndexedDB`, error);
      throw error;
    }
  }

  /**
   * 根據 ID 獲取單一實體
   *
   * @param id - 實體 ID
   * @returns 實體或 null
   */
  async getById(id: string): Promise<T | null> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(id);

        request.onsuccess = () => {
          if (request.result) {
            resolve(this.deserialize(request.result));
          } else {
            resolve(null);
          }
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to get ${this.storeName} by ID`, error);
      throw error;
    }
  }

  /**
   * 獲取用戶的所有實體
   *
   * @param userId - 用戶 ID
   * @param options - 查詢選項
   * @returns 實體陣列
   */
  async getAll(userId: string, options?: QueryOptions): Promise<T[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          let results = request.result.map((item: CachedEntity<T>) => this.deserialize(item));

          // 應用 limit 和 offset
          if (options?.offset) {
            results = results.slice(options.offset);
          }
          if (options?.limit) {
            results = results.slice(0, options.limit);
          }

          resolve(results);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to get all ${this.storeName}`, error);
      throw error;
    }
  }

  /**
   * 本地創建實體（離線優先）
   *
   * @param entity - 實體資料
   */
  async createLocal(entity: T): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      const prepared = this.prepareForStorage(entity);

      return new Promise((resolve, reject) => {
        const request = store.put(prepared);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to create ${this.storeName} locally`, error);
      throw error;
    }
  }

  /**
   * 本地更新實體（離線優先）
   *
   * @param id - 實體 ID
   * @param updates - 更新的欄位
   */
  async updateLocal(id: string, updates: Partial<T>): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const getRequest = store.get(id);

        getRequest.onsuccess = () => {
          const existing = getRequest.result;

          if (!existing) {
            // 不存在則拒絕更新（避免意外創建不完整的實體）
            reject(new Error(`Entity with id ${id} not found in ${this.storeName}`));
            return;
          }

          // 反序列化現有資料，合併更新，再序列化存儲
          const deserialized = this.deserialize(existing);
          const merged = { ...deserialized, ...updates };
          // null 語義為「明確清空」：從物件中移除該 key，避免舊值殘留
          Object.entries(updates as Record<string, unknown>).forEach(([key, value]) => {
            if (value === null) delete merged[key as keyof typeof merged];
          });
          const prepared = this.prepareForStorage(merged);

          const putRequest = store.put(prepared);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        };

        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      logError(`Failed to update ${this.storeName} locally`, error);
      throw error;
    }
  }

  /**
   * 本地刪除實體（離線優先）
   *
   * @param id - 實體 ID
   */
  async deleteLocal(id: string): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => {
          logInfo(`${this.storeName} deleted from IndexedDB`, { id });
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to delete ${this.storeName} locally`, error);
      throw error;
    }
  }

  /**
   * 替換臨時 ID 為伺服器 ID
   * 用於離線創建後同步
   *
   * @param tempId - 臨時 ID
   * @param realId - 真實的伺服器 ID
   */
  async replaceTempId(tempId: string, realId: string): Promise<void> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const getRequest = store.get(tempId);

        getRequest.onsuccess = () => {
          if (getRequest.result) {
            const entity = getRequest.result;
            entity.id = realId;

            // 刪除舊的臨時 ID 記錄
            store.delete(tempId);

            // 添加新的真實 ID 記錄
            const putRequest = store.put(entity);

            putRequest.onsuccess = () => {
              logInfo(`${this.storeName} temp ID replaced`, { tempId, realId });
              resolve();
            };

            putRequest.onerror = () => reject(putRequest.error);
          } else {
            logWarn(`${this.storeName} with temp ID not found`, { tempId });
            resolve(); // 不報錯，可能已經同步過了
          }
        };

        getRequest.onerror = () => reject(getRequest.error);
      });
    } catch (error) {
      logError(`Failed to replace ${this.storeName} temp ID`, error);
      throw error;
    }
  }

  /**
   * 清理過期的快取資料
   *
   * @param userId - 用戶 ID
   * @param options - 清理選項
   * @returns 清理結果
   */
  async cleanupExpired(userId: string, options?: CleanupOptions): Promise<CleanupResult> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      const retentionDays = options?.retentionDays ?? this.defaultRetentionDays;
      const cutoffDate = options?.beforeDate ?? new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000);
      const cutoffTime = cutoffDate.getTime();

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          const allEntities = request.result;
          let deletedCount = 0;
          let scannedCount = allEntities.length;

          for (const entity of allEntities) {
            if (entity.cachedAt && entity.cachedAt < cutoffTime) {
              store.delete(entity.id);
              deletedCount++;
            }
          }

          tx.oncomplete = () => {
            logInfo(`${this.storeName} cleanup completed`, {
              deletedCount,
              remainingCount: scannedCount - deletedCount,
              scannedCount,
            });

            resolve({
              deletedCount,
              remainingCount: scannedCount - deletedCount,
              scannedCount,
            });
          };

          tx.onerror = () => reject(tx.error);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to cleanup ${this.storeName}`, error);
      throw error;
    }
  }

  /**
   * 獲取快取統計資訊
   *
   * @param userId - 用戶 ID
   * @returns 快取統計
   */
  async getCacheStats(userId: string): Promise<CacheStats> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const index = store.index('userId');

      return new Promise((resolve, reject) => {
        const request = index.getAll(userId);

        request.onsuccess = () => {
          const entities = request.result;
          const totalCount = entities.length;

          if (totalCount === 0) {
            resolve({
              totalCount: 0,
              oldestCachedAt: null,
              newestCachedAt: null,
              avgCacheAge: null,
            });
            return;
          }

          const cachedTimes = entities
            .map((e: CachedEntity<T>) => e.cachedAt)
            .filter((t): t is number => typeof t === 'number');

          const oldestCachedAt = Math.min(...cachedTimes);
          const newestCachedAt = Math.max(...cachedTimes);
          const avgCacheAge = cachedTimes.reduce((sum, t) => sum + t, 0) / cachedTimes.length;

          resolve({
            totalCount,
            oldestCachedAt,
            newestCachedAt,
            avgCacheAge,
          });
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      logError(`Failed to get ${this.storeName} cache stats`, error);
      throw error;
    }
  }
}
